import { useMemo } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type {
	AnyService,
	DependencyOverridesOf,
	ToResolvedService,
	ServiceResolverProviding,
} from '@atlassian/expansion-services/types';
import { useServiceCore } from '@atlassian/expansion-services/core';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { cfetchJSON } from './services/fetch.tsx';
import type { FetchInput, FetchResponse } from '@atlassian/expansion-services/services';

const GasEventTypeToConfluenceEventType = {
	track: 'sendTrackEvent',
	ui: 'sendUIEvent',
	screen: 'sendScreenEvent',
	operational: 'sendOperationalEvent',
} as const;
type GasEventType = keyof typeof GasEventTypeToConfluenceEventType;

const toConfluenceAnalyticsEventPayload = (
	payload: AnalyticsEventPayload,
): AnalyticsEventPayload => {
	// expansion_services_invite_in_core_invites: expansion-services fires this flat shape
	// instead of { type, data } when that gate is on. Remove this translation (and
	// GasEventTypeToConfluenceEventType) once the gate is fully ramped and expansion-services
	// no longer sends { type, data }.
	if (!('eventType' in payload)) {
		return payload;
	}
	const { eventType, ...data } = payload as { eventType: GasEventType } & Record<string, unknown>;
	return { type: GasEventTypeToConfluenceEventType[eventType], data };
};

export const useServiceEnvironmentDeps = (): {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	fetchJSON: <Response>(input: FetchInput) => Promise<FetchResponse<Response>>;
	getCurrentCloudId: () => string;
	getCurrentProduct: () => 'confluence';
	getCurrentUserId: () => string | null;
} => {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent: createAnalyticsEventBridge } = useAnalyticsEvents();

	return useMemo(() => {
		const createAnalyticsEvent: CreateUIAnalyticsEvent = (payload) =>
			createAnalyticsEventBridge(toConfluenceAnalyticsEventPayload(payload));

		return {
			createAnalyticsEvent,
			fetchJSON: cfetchJSON,
			getCurrentCloudId: () => cloudId,
			getCurrentProduct: () => 'confluence',
			getCurrentUserId: () => userId,
		};
	}, [createAnalyticsEventBridge, cloudId, userId]);
};

type EnvironmentDeps = ReturnType<typeof useServiceEnvironmentDeps>;

export const useService: ServiceResolverProviding<EnvironmentDeps> = <
	DeclaredService extends AnyService,
>(
	service: DeclaredService,
	providedDeps?: DependencyOverridesOf<DeclaredService, EnvironmentDeps>,
): ToResolvedService<DeclaredService> => {
	const environmentDeps = useServiceEnvironmentDeps();
	const deps = useMemo(
		() => ({ ...environmentDeps, ...providedDeps }),
		[environmentDeps, providedDeps],
	);

	return useServiceCore(service, deps as never);
};
